/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // For off-session bookings (>90 days away), customers can choose to book now and pay later.
  // These transitions save the booking without immediate payment.
  REQUEST_BOOKING: 'transition/request-booking',
  REQUEST_BOOKING_AFTER_INQUIRY: 'transition/request-booking-after-inquiry',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // When the provider accepts or declines a pending off-session booking request
  ACCEPT_PENDING: 'transition/accept-pending',
  DECLINE_PENDING: 'transition/decline-pending',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The operator can accept or decline pending off-session booking requests
  OPERATOR_ACCEPT_PENDING: 'transition/operator-accept-pending',
  OPERATOR_DECLINE_PENDING: 'transition/operator-decline-pending',

  // The backend automatically expires pending off-session bookings if not accepted in time
  // (6 days after entering pending state, or 1 day before booking start — whichever is earlier)
  EXPIRE_PENDING: 'transition/expire-pending',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  // Off-session additions
  REQUEST_OFFSESSION: 'transition/request-offsession',
  CANCEL_OFFSESSION: 'transition/cancel-offsession',
  MARK_PENDING_PAYMENT: 'transition/mark-pending-payment',
  OPERATOR_TRIGGER_PAYMENT: 'transition/operator-trigger-payment',
  AUTO_PAYMENT: 'transition/auto-payment',
  INITIATE_MANUAL_PAYMENT: 'transition/initiate-manual-payment',
  CONFIRM_MANUAL_PAYMENT: 'transition/confirm-manual-payment',
  CANCEL_AWAITING_PAYMENT: 'transition/cancel-awaiting-payment',

  // Free booking transitions (no Stripe at all)
  FREE_BOOKING: 'transition/free-booking',
  FREE_BOOKING_AFTER_INQUIRY: 'transition/free-booking-after-inquiry',
  ACCEPT_FREE: 'transition/accept-free',
  OPERATOR_ACCEPT_FREE: 'transition/operator-accept-free',
  DECLINE_FREE: 'transition/decline-free',
  OPERATOR_DECLINE_FREE: 'transition/operator-decline-free',
  EXPIRE_FREE: 'transition/expire-free',
  COMPLETE_FREE: 'transition/complete-free',
  OPERATOR_COMPLETE_FREE: 'transition/operator-complete-free',
  CANCEL_FREE: 'transition/cancel-free',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  REQUESTED_OFFSESSION: 'requested-offsession',
  PENDING: 'pending', // Off-session bookings (card saved, payment pending)
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  AWAITING_PAYMENT: 'awaiting-payment', // Off-session: provider accepted, waiting for payment trigger
  PENDING_CONFIRMATION: 'pending-confirmation', // Off-session: manual payment initiated, waiting for confirmation
  FREE_PENDING: 'free-pending', // Free bookings awaiting provider approval
  FREE_ACCEPTED: 'free-accepted', // Free bookings accepted by provider
  FREE_EXPIRED: 'free-expired', // Free bookings expired (provider didn't respond)
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  CANCELLED: 'cancelled',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_BOOKING]: states.PENDING, // Off-session booking transition
        [transitions.REQUEST_OFFSESSION]: states.REQUESTED_OFFSESSION,
        [transitions.FREE_BOOKING]: states.FREE_PENDING, // Free booking awaits provider approval
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_BOOKING_AFTER_INQUIRY]: states.PENDING, // Off-session booking after inquiry
        [transitions.FREE_BOOKING_AFTER_INQUIRY]: states.FREE_PENDING, // Free booking after inquiry
      },
    },
    [states.REQUESTED_OFFSESSION]: {
      on: {
        [transitions.CANCEL_OFFSESSION]: states.CANCELED,
        [transitions.MARK_PENDING_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING]: {
      on: {
        // Off-session bookings can be accepted or declined by provider
        [transitions.ACCEPT_PENDING]: states.AWAITING_PAYMENT,
        [transitions.DECLINE_PENDING]: states.DECLINED,
        [transitions.OPERATOR_ACCEPT_PENDING]: states.AWAITING_PAYMENT,
        [transitions.OPERATOR_DECLINE_PENDING]: states.DECLINED,
        // Auto-expire if provider doesn't respond in time
        [transitions.EXPIRE_PENDING]: states.EXPIRED,
      },
    },

    [states.AWAITING_PAYMENT]: {
      on: {
        [transitions.MARK_PENDING_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.OPERATOR_TRIGGER_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.CANCEL_AWAITING_PAYMENT]: states.CANCELED,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.AUTO_PAYMENT]: states.ACCEPTED,
        [transitions.INITIATE_MANUAL_PAYMENT]: states.PENDING_CONFIRMATION,
      },
    },

    [states.PENDING_CONFIRMATION]: {
      on: {
        [transitions.CONFIRM_MANUAL_PAYMENT]: states.ACCEPTED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
      },
    },

    [states.FREE_PENDING]: {
      on: {
        [transitions.ACCEPT_FREE]: states.FREE_ACCEPTED,
        [transitions.OPERATOR_ACCEPT_FREE]: states.FREE_ACCEPTED,
        [transitions.DECLINE_FREE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE_FREE]: states.DECLINED,
        [transitions.EXPIRE_FREE]: states.FREE_EXPIRED,
      },
    },

    [states.FREE_EXPIRED]: {},

    [states.FREE_ACCEPTED]: {
      on: {
        [transitions.COMPLETE_FREE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_FREE]: states.DELIVERED,
        [transitions.CANCEL_FREE]: states.CANCELLED,
      },
    },

    [states.CANCELED]: {},
    [states.CANCELLED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.MARK_PENDING_PAYMENT,
    transitions.AUTO_PAYMENT,
    transitions.INITIATE_MANUAL_PAYMENT,
    transitions.CONFIRM_MANUAL_PAYMENT,
    transitions.CANCEL_OFFSESSION,
    transitions.FREE_BOOKING,
    transitions.FREE_BOOKING_AFTER_INQUIRY,
    transitions.ACCEPT_FREE,
    transitions.OPERATOR_ACCEPT_FREE,
    transitions.DECLINE_FREE,
    transitions.OPERATOR_DECLINE_FREE,
    transitions.EXPIRE_FREE,
    transitions.COMPLETE_FREE,
    transitions.OPERATOR_COMPLETE_FREE,
    transitions.CANCEL_FREE,
    transitions.CANCEL_AWAITING_PAYMENT,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_BOOKING,
    transitions.REQUEST_BOOKING_AFTER_INQUIRY,
    transitions.OPERATOR_TRIGGER_PAYMENT,
    transitions.FREE_BOOKING,
    transitions.FREE_BOOKING_AFTER_INQUIRY,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.CANCEL_AWAITING_PAYMENT,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
